/**
 * 事件種別のラベルマップ (enum → 日本語表示)
 *
 * 設計:
 *   - **CASE_TYPE_LABELS**: 現行 (canonical) の正規 enum key → ラベル。
 *     `CASE_TYPE_OPTIONS` の起点になるため、新規 caseType を追加する場合は
 *     この map に追加すれば create / filter dropdown にも自動反映される。
 *   - **LEGACY_CASE_TYPE_ALIASES**: 過去に DB に保存された **legacy enum key**
 *     を canonical key に正規化するための **読み取り専用** map。
 *     dropdown に重複を出さないため `CASE_TYPE_OPTIONS` には反映しない。
 *     表示時のみ `caseTypeLabel()` 経由で参照する。
 *
 * 全ページで共通使用。新しい事件種別を追加する場合は CASE_TYPE_LABELS に追記。
 * 既に保存されたデータの legacy enum を後付けで救済する場合は
 * LEGACY_CASE_TYPE_ALIASES に追記する。
 *
 * 参照: PR-13 Group A (Dev UI raw value audit) — root cause は legacy seed
 *       データの caseType が `bankruptcy_simultaneous` 等の旧 enum 名で
 *       保存されており、CASE_TYPE_LABELS の canonical key と不一致のため
 *       UI に raw 文字列が露出していた。DB migration を伴わずに UI 側だけで
 *       救済する。
 */
export const CASE_TYPE_LABELS: Record<string, string> = {
  // 破産系
  bankruptcy_dohai: '自己破産(同廃)',
  bankruptcy_kanzai: '自己破産(管財)',
  // 再生系
  individual_rehabilitation: '個人再生',
  // notice-rehab-case-type-20260916: 受任通知は住宅ローン特則の有無でテンプレートが違う (BE notice-variant.ts)。
  // 「個人再生」だけでは種類を決められないため、新規はこの 2 つを選ぶ (既存の「個人再生」はそのまま表示)
  civil_rehab_no_housing: '個人再生（住宅ローン特則なし）',
  civil_rehab_housing: '個人再生（住宅ローン特則あり）',
  // 債務整理系
  overpayment: '過払い金',
  voluntary_arrangement: '任意整理',
  // その他 (QA-005 で追加)
  labor: '労働問題',
  divorce: '離婚',
  traffic: '交通事故',
  inheritance: '相続',
  corporate: '法人案件',
  other: 'その他',
};

/**
 * Legacy enum key → canonical key の alias map (PR-13 Group A)。
 *
 * 既存 Dev データには下記のような **古い enum key** で caseType が保存された
 * レコードが残っている (例: `bankruptcy_simultaneous`)。
 * DB migration を伴わずに UI 表示だけ救済するため、表示時に alias を解決して
 * canonical key に正規化し、`CASE_TYPE_LABELS` から日本語ラベルを引き出す。
 *
 * **注意**: ここに alias を追加しても `CASE_TYPE_OPTIONS` には載らない。
 * 新規 case を作るときは canonical key のみが選択肢に出る。
 */
export const LEGACY_CASE_TYPE_ALIASES: Record<string, string> = {
  // 自己破産 (同時廃止) — legacy: bankruptcy_simultaneous → canonical: bankruptcy_dohai
  bankruptcy_simultaneous: 'bankruptcy_dohai',
  // 自己破産 (管財) — legacy: bankruptcy_managed → canonical: bankruptcy_kanzai
  bankruptcy_managed: 'bankruptcy_kanzai',
  // 個人再生 — legacy: civil_rehabilitation → canonical: individual_rehabilitation
  civil_rehabilitation: 'individual_rehabilitation',
  // 法人案件 — legacy: corporate_bankruptcy → canonical: corporate
  corporate_bankruptcy: 'corporate',
};

/** 事件種別の選択肢 (select/option 用) */
export const CASE_TYPE_OPTIONS = Object.entries(CASE_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/** unknown caseType を出すときの fallback (PR-13 Group A) */
export const CASE_TYPE_UNKNOWN_FALLBACK = '事件種別未設定';

/**
 * caseType 値 (任意の文字列) を canonical な enum key に正規化する。
 *
 * - canonical key (`bankruptcy_dohai` 等) → そのまま返す
 * - legacy alias (`bankruptcy_simultaneous` 等) → 対応する canonical key を返す
 * - それ以外 (null / undefined / 未登録の文字列) → 元の値をそのまま返す
 *
 * フィルタ等で「canonical を選んだら legacy data も拾う」用途に使う:
 *   const normalized = normalizeCaseType(record.caseType);
 *   if (normalized === filterValue) ...
 */
export function normalizeCaseType(value: string | null | undefined): string {
  if (!value) return '';
  if (CASE_TYPE_LABELS[value]) return value;
  return (
    LEGACY_CASE_TYPE_ALIASES[value] ??
    Object.keys(CASE_TYPE_LABELS).find((key) => CASE_TYPE_LABELS[key] === value) ??
    value
  );
}

/**
 * caseType 値を日本語ラベルに解決する共通 formatter (PR-13 Group A)。
 *
 * 解決順:
 *   1. CASE_TYPE_LABELS に直接ヒット (canonical) → ラベル
 *   2. LEGACY_CASE_TYPE_ALIASES でエイリアス → CASE_TYPE_LABELS → ラベル
 *   3. 値が既に Japanese (legacy で日本語が保存されたケース、例: `相続`) →
 *      CASE_TYPE_LABELS の value 集合に含まれていれば、その日本語をそのまま返す
 *   4. それ以外 (null / undefined / 未登録の英 raw enum) → fallback
 *
 * fallback の既定は `CASE_TYPE_UNKNOWN_FALLBACK` (`事件種別未設定`)。
 * 場面によって `''` を返したいときは第 2 引数で空文字を渡せる。
 */
export function caseTypeLabel(
  value: string | null | undefined,
  fallback: string = CASE_TYPE_UNKNOWN_FALLBACK,
): string {
  if (!value) return fallback;
  // 1. canonical key
  const direct = CASE_TYPE_LABELS[value];
  if (direct) return direct;
  // 2. legacy alias
  const aliased = LEGACY_CASE_TYPE_ALIASES[value];
  if (aliased) {
    const aliasedLabel = CASE_TYPE_LABELS[aliased];
    if (aliasedLabel) return aliasedLabel;
  }
  // 3. 既存 Japanese (例: `相続` のような label 値そのものが DB に入っているケース)。
  //    label-value 集合と一致するならそのまま表示。
  //    O(N) だが case type は数 10 件オーダなので問題なし。
  for (const label of Object.values(CASE_TYPE_LABELS)) {
    if (label === value) return label;
  }
  // 4. それ以外は fallback (raw enum を UI に晒さない)
  return fallback;
}

/**
 * 1 つの canonical key に対応するフィルタ対象値の一覧を返す (PR-13 Group A)。
 *
 * 例:
 *   caseTypeFilterValues('bankruptcy_dohai')
 *     → ['bankruptcy_dohai', 'bankruptcy_simultaneous']
 *
 * BE filter が 1 値しか受け付けない既存仕様のもとでは、本関数の返り値を
 * 直接渡せないが、client-side post-filter
 * (`array.filter((x) => values.includes(x.caseType))`) 等で legacy data も
 * 拾うために使える。dropdown UX の bloat を避けるため、`CASE_TYPE_OPTIONS`
 * には alias を含めずに本関数で展開する設計。
 */
export function caseTypeFilterValues(canonical: string): string[] {
  const values: string[] = [canonical];
  for (const [legacy, target] of Object.entries(LEGACY_CASE_TYPE_ALIASES)) {
    if (target === canonical) values.push(legacy);
  }
  return values;
}
